import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  useDecommissionGateway,
  useEnrollGateway,
  useGatewayFleet,
  useGatewayFleetSummary,
} from "@/api/queries/gateway-fleet";
import { useCurrentUser } from "@/api/queries/me";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";
import {
  credentialEventLabel,
  gatewayStatusVariant,
  sortedStatusCounts,
  titleCaseStatus,
} from "@/lib/gateway-fleet";

export function GatewayFleetPage() {
  const { identity } = useAuth();

  if (!isRespaiAdmin(identity?.role)) {
    return (
      <p className="text-sm text-destructive">
        Gateway fleet management is restricted to RespAI platform administrators.
      </p>
    );
  }

  return <GatewayFleetPageInner />;
}

function GatewayFleetPageInner() {
  const me = useCurrentUser();
  const tz = me.data?.department?.timezone;
  const [showRegister, setShowRegister] = useState(false);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Gateway fleet</h1>
          <p className="text-sm text-muted-foreground">
            XP8 gateway devices enrolled for{" "}
            <span className="font-medium text-foreground">
              {me.data?.department?.name ?? "your department"}
            </span>
            . Enroll a gateway here, then paste its key into the device's setup
            screen.
          </p>
        </div>
        <Button onClick={() => setShowRegister((v) => !v)}>
          {showRegister ? "Cancel" : "Register gateway"}
        </Button>
      </header>

      {showRegister && (
        <RegisterGatewayCard onClose={() => setShowRegister(false)} />
      )}

      <SummaryCard />
      <FleetCard tz={tz} />
    </div>
  );
}

function RegisterGatewayCard({ onClose }: { onClose: () => void }) {
  const [serial, setSerial] = useState("");
  const enroll = useEnrollGateway();
  const credential = enroll.data?.credential;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Register a gateway</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {credential ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Gateway{" "}
              <span className="font-mono text-foreground">
                {enroll.data?.serial}
              </span>{" "}
              enrolled. On the XP8, open{" "}
              <span className="font-medium text-foreground">
                Device setup (admin)
              </span>{" "}
              and paste this into the Gateway API key field:
            </p>
            <div className="max-w-md space-y-1 rounded border border-border bg-muted/40 p-2">
              <code className="block break-all font-mono text-xs">
                {credential}
              </code>
              <p className="text-xs text-muted-foreground">
                Copy now: shown once only.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={onClose}>
              Done
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              Gateway serial
              <Input
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                placeholder="e.g. XP8-BURN-01"
                className="mt-1 w-64"
              />
            </label>
            <Button
              onClick={() => serial.trim() && enroll.mutate(serial.trim())}
              disabled={!serial.trim() || enroll.isPending}
            >
              {enroll.isPending ? "Enrolling…" : "Enroll gateway"}
            </Button>
          </div>
        )}
        {enroll.isError && (
          <p className="text-sm text-destructive">
            {enroll.error instanceof Error
              ? enroll.error.message
              : "Could not enroll gateway."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryCard() {
  const summary = useGatewayFleetSummary();
  const data = summary.data;
  const statusCounts = sortedStatusCounts(data?.by_status);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fleet summary</CardTitle>
      </CardHeader>
      <CardContent>
        {summary.isLoading ? (
          <Skeleton className="h-20" />
        ) : data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Total" value={data.total} />
              <Stat label="Bound" value={data.bound} />
              <Stat label="Unbound" value={data.unbound} />
            </div>
            {statusCounts.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {statusCounts.map(([status, count]) => (
                  <Badge key={status} variant={gatewayStatusVariant(status)}>
                    {titleCaseStatus(status)}: {count}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No fleet data yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function FleetCard({ tz }: { tz?: string }) {
  const fleet = useGatewayFleet();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Enrolled gateways</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {fleet.isLoading ? (
          <div className="space-y-2 p-6">
            <Skeleton className="h-6" />
            <Skeleton className="h-6" />
          </div>
        ) : fleet.data && fleet.data.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>Serial</Th>
                <Th>Status</Th>
                <Th>Bound sensor</Th>
                <Th>Last credential event</Th>
                <Th>Enrolled</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fleet.data.map((g) => (
                <Tr key={g.gateway_id}>
                  <Td className="font-mono text-sm">{g.serial}</Td>
                  <Td>
                    <Badge variant={gatewayStatusVariant(g.status)}>
                      {titleCaseStatus(g.status)}
                    </Badge>
                  </Td>
                  <Td>
                    {g.bound_sensor_device_id ? (
                      <span className="font-mono text-xs">
                        {g.bound_sensor_device_id.slice(0, 8)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Unbound</span>
                    )}
                  </Td>
                  <Td className="text-sm">
                    {g.last_credential_event_type ? (
                      <span>
                        {credentialEventLabel(g.last_credential_event_type)}
                        {g.last_credential_event_at ? (
                          <span className="text-muted-foreground">
                            {" · "}
                            {formatDateTime(g.last_credential_event_at, tz)}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </Td>
                  <Td className="text-sm">{formatDateTime(g.enrolled_at, tz)}</Td>
                  <Td>
                    <RemoveGatewayButton serial={g.serial} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <p className="p-6 text-sm text-muted-foreground">
            No gateways enrolled yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function RemoveGatewayButton({ serial }: { serial: string }) {
  const [confirming, setConfirming] = useState(false);
  const decommission = useDecommissionGateway();

  if (confirming) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Remove?</span>
          <Button
            variant="destructive"
            size="sm"
            disabled={decommission.isPending}
            onClick={() =>
              decommission.mutate(serial, {
                onSuccess: () => setConfirming(false),
              })
            }
          >
            {decommission.isPending ? "Removing…" : "Confirm"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={decommission.isPending}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </Button>
        </div>
        {decommission.isError && (
          <span className="text-xs text-destructive">
            {decommission.error instanceof Error
              ? decommission.error.message
              : "Could not remove gateway."}
          </span>
        )}
      </div>
    );
  }

  return (
    <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
      Remove
    </Button>
  );
}
